import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, Play } from 'lucide-react'

const videoData = [
  {
    id: 'VDzgRY23OHc',
    title: 'NIFTYVILLE',
    role: 'TECHNICAL DIRECTOR',
    url: 'https://youtu.be/VDzgRY23OHc'
  },
  {
    id: 'nwywfZuhHH4',
    title: 'HELIX METAVERSE',
    role: 'TECHNICAL DIRECTOR',
    url: 'https://www.youtube.com/watch?v=nwywfZuhHH4'
  },
  {
    id: 'KNTVpHT-Xgk',
    title: 'SHRAPNEL',
    role: 'SENIOR C++ DEVELOPER',
    url: 'https://youtu.be/KNTVpHT-Xgk'
  },
  {
    id: 'N3bVjC8Qhps',
    title: 'GEARSTORM',
    role: 'C++ DEVELOPER',
    url: 'https://youtu.be/N3bVjC8Qhps'
  },
  {
    id: 'zOOd1SEcE1Q',
    title: 'BULLETVILLE',
    role: 'C++ DEVELOPER',
    url: 'https://youtu.be/zOOd1SEcE1Q'
  },
  {
    id: 'JF-I_Nnkes8',
    title: 'SHORES OF PLUNDER',
    role: 'LEAD DEVELOPER',
    url: 'https://www.youtube.com/watch?v=JF-I_Nnkes8'
  },
  {
    id: 'HqWFVBFMbdE',
    title: 'PINK SLIP CITY',
    role: 'SENIOR LEAD DEVELOPER',
    url: 'https://youtu.be/HqWFVBFMbdE'
  },
  {
    id: 'O-3HFFOkNVU',
    title: 'HARDKNOCK NIGHTS',
    role: 'LEAD DEVELOPER',
    url: 'https://www.youtube.com/watch?v=O-3HFFOkNVU'
  },
  {
    id: '7rCHaEFu4_w',
    title: 'MORTARMEN',
    role: 'LEAD DEVELOPER',
    url: 'https://www.youtube.com/watch?v=7rCHaEFu4_w'
  }
]

const YouTubeCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  // Group videos into sets of 3
  const videoGroups = []
  for (let i = 0; i < videoData.length; i += 3) {
    videoGroups.push(videoData.slice(i, i + 3))
  }

  // Auto-advance carousel
  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % videoGroups.length)
      }, 8000) // Change every 8 seconds
      return () => clearInterval(interval)
    }
  }, [isPaused, videoGroups.length])

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + videoGroups.length) % videoGroups.length)
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % videoGroups.length)
  }

  const goToGroup = (index) => {
    setCurrentIndex(index)
  }

  const currentGroup = videoGroups[currentIndex] || []

  return (
    <div 
      className="relative w-full bg-base-100 overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Navigation Arrows */}
      {videoGroups.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 btn btn-circle btn-primary glass shadow-lg hover:scale-110 transition-transform"
            aria-label="Previous videos"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 btn btn-circle btn-primary glass shadow-lg hover:scale-110 transition-transform"
            aria-label="Next videos"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Video Grid Container */}
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <h2 className="text-4xl font-bold mb-2 text-gradient flex items-center justify-center gap-3">
            <Play className="w-10 h-10 text-primary" />
            Project Showcase
          </h2>
          <p className="text-base-content/60">Featured work from my professional journey</p>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {currentGroup.map((video, idx) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="card glass p-4 hover:glow-effect transition-all"
              >
                <div className="aspect-video mb-4 rounded-lg overflow-hidden bg-base-200">
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}`}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-xl font-bold text-base-content mb-1">{video.title}</h3>
                  <p className="text-sm text-primary font-semibold">{video.role}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Dots Indicator */}
        {videoGroups.length > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            {videoGroups.map((_, index) => (
              <button
                key={index}
                onClick={() => goToGroup(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-primary w-8 shadow-lg shadow-primary/50'
                    : 'bg-base-content/30 hover:bg-primary/50 w-2'
                }`}
                aria-label={`Go to group ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default YouTubeCarousel
